'use client';

import React from 'react';

import { Breadcrumb as PartnerBreadcrumb } from '@monetization/hpaip-components-library';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { sendAnalytics } from '@storefront/app/analytics/SimpleTriggerEvent';
import {
  defaultURL,
  RequiredPartnerData,
} from '@storefront/app/pdp-config/shared-components/header/header';
import useGetPartnerCompensation from '@storefront/hooks/useGetPartnerCompensation';
import usePartner from '@storefront/hooks/usePartner';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import UserSessionAvatar from '@storefront/shared-components/user-session-avatar/user-session-avatar';
import { AuthzJWT } from '@storefront/utils/api/auth';
import { buildPath } from '@storefront/utils/utils';

import styles from './header.module.scss';

interface HeaderProps {
  session?: AuthzJWT | null;
}

const getPageName = (pathname: string): string => {
  if (pathname?.includes('/cart')) return 'SummaryPage';
  if (pathname?.includes('/checkout')) return 'CheckoutOnePage';
  if (pathname?.includes('/order')) return 'OrderConfirmation';
  return pathname;
};

export const Header = ({ session }: HeaderProps) => {
  const { hpChannel } = usePartner();
  const { data, error } = useGetPartnerCompensation(hpChannel);
  const isLoading = data === undefined && !error;
  const isValidPartner =
    !!hpChannel &&
    hpChannel.trim() !== '' &&
    !!data?.getPartnerCompensation?.hp_partner_name;
  const pageName = getPageName(usePathname());

  const onClickHandler = async () => {
    const partnerUrl =
      data?.getPartnerCompensation?.hp_off_ramp_redirect_url ?? defaultURL;
    const partnerName =
      data?.getPartnerCompensation?.hp_partner_name ?? 'HpStore';
    await sendAnalytics({
      simpleUiEvent: uiEvents.breadcrumbEvent,
      override: {
        controlName: 'Partner',
        controlDetail: `partnerUrl=${partnerUrl}&name=${partnerName}`,
        screenName: pageName,
      },
    });
  };

  return (
    <div
      className={styles['top-bar']}
      data-testid="logo-element"
      id="logo-element"
    >
      <div className={styles['container']}>
        <div>
          {isValidPartner && (
            <div className={styles['partner-breadcrumb']}>
              <PartnerBreadcrumb
                partnerCompensation={{ data: data as RequiredPartnerData }}
                error={error}
                onClick={onClickHandler}
                defaultURL={defaultURL}
                clientReferrer={''}
              />
            </div>
          )}
          {!isValidPartner && !isLoading && (
            <Image
              src={buildPath('HPLogo-blue.svg')}
              className={styles['logo']}
              width="60"
              height="60"
              alt="HP All-In Plan logo"
            />
          )}
        </div>
      </div>
      {session?.user && <UserSessionAvatar />}
    </div>
  );
};

export default Header;
